import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { authGuard } from './auth-guard';

export { authGuard };

declare module 'vue-router' {
  interface RouteMeta {
    /** ドキュメントタイトル。afterEach で「{title} | GRAND Map Maker」へ整形する。 */
    title?: string;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/HubView.vue'),
  },
  {
    path: '/properties',
    name: 'PropertyList',
    component: () => import('@/views/PropertyList.vue'),
    meta: { title: '避難マップ' },
  },
  {
    path: '/properties/new',
    name: 'PropertyNew',
    component: () => import('@/views/PropertyNew.vue'),
    meta: { title: '避難マップ 新規作成' },
  },
  {
    path: '/properties/batch',
    name: 'PropertyBatch',
    component: () => import('@/views/PropertyBatch.vue'),
    meta: { title: '避難マップ 一括登録' },
  },
  {
    path: '/properties/:id/edit',
    name: 'PropertyEditor',
    component: () => import('@/views/PropertyEditor.vue'),
    props: true,
    meta: { title: '避難マップ 編集' },
  },
  {
    // 物件マスタの取り込み (管理用)。画面からはリンクしない — URL を直接開いて使う。
    path: '/admin/building-master',
    name: 'BuildingMasterAdmin',
    component: () => import('@/views/admin/BuildingMasterAdmin.vue'),
    meta: { title: '物件マスタの管理' },
  },
  {
    // 取込CSV作成スキルの仕様書 (一括登録ページの「CSV の作り方」から開く)
    path: '/csv-skill',
    name: 'CsvSkillGuide',
    component: () => import('@/views/CsvSkillGuide.vue'),
    meta: { title: 'CSV の作り方' },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: 'ログイン' },
  },
  {
    path: '/evacuation-sites',
    name: 'EvacuationSitesList',
    component: () => import('@/views/evacuation-sites/PropertyListV2.vue'),
    meta: { title: '災害時避難先' },
  },
  {
    path: '/evacuation-sites/batch',
    name: 'EvacuationSitesBatch',
    component: () => import('@/views/evacuation-sites/PropertyBatchV2.vue'),
    meta: { title: '災害時避難先 一括登録' },
  },
  {
    path: '/evacuation-sites/new',
    name: 'EvacuationSitesNew',
    component: () => import('@/views/evacuation-sites/PropertyNewV2.vue'),
    meta: { title: '災害時避難先 新規作成' },
  },
  {
    path: '/evacuation-sites/:id/edit',
    name: 'EvacuationSitesEditor',
    component: () => import('@/views/evacuation-sites/PropertyEditorV2.vue'),
    meta: { title: '災害時避難先 編集' },
  },
  {
    path: '/evacuation-sites/:id/render',
    name: 'EvacuationSitesRender',
    component: () => import('@/views/evacuation-sites/PropertyRenderV2.vue'),
  },
  {
    path: '/smoking',
    name: 'SmokingList',
    component: () => import('@/views/smoking/SmokingList.vue'),
    meta: { title: '喫煙所マップ' },
  },
  {
    path: '/smoking/new',
    name: 'SmokingNew',
    component: () => import('@/views/smoking/SmokingNew.vue'),
    meta: { title: '喫煙所マップ 新規作成' },
  },
  {
    path: '/smoking/:id/edit',
    name: 'SmokingEditor',
    component: () => import('@/views/smoking/SmokingEditor.vue'),
    meta: { title: '喫煙所マップ 編集' },
  },
  {
    path: '/setup-map',
    name: 'SetupMap',
    component: () => import('@/views/setupmap/SetupMapView.vue'),
    meta: { title: '設置場所マップ' },
  },
  {
    path: '/setup-map/export',
    name: 'SetupMapExport',
    component: () => import('@/views/setupmap/SvgExportView.vue'),
    meta: { title: '設置場所マップ 書き出し' },
  },
  {
    // 開発用: 駅事業者アイコン / line-icons の目視確認ページ。
    path: '/dev/station-icons',
    name: 'DevStationIcons',
    component: () => import('@/views/dev/StationIconsGallery.vue'),
    meta: { title: '駅アイコン (dev)' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { title: 'ページが見つかりません' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => authGuard(to));

/** ページごとにドキュメントタイトルを整える (ブラウザタブ・リンク共有時の見出し)。 */
const SITE_NAME = 'GRAND Map Maker';
router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} | ${SITE_NAME}` : SITE_NAME;
});

export default router;
